import axiosInstance from '@/plugins/axios'; // eslint-disable-line import/no-cycle
import {
  buildInsightFromIndicator,
  buildLimitedInsightFromIndicator,
  pickPeriodPair,
  buildDataQueryParams,
  idsMatch,
} from './signalEngineCore';

const buildContext = (filters) => ({
  indicatorName: filters.indicator,
  locationName: filters.location,
  datasource: filters.datasource,
  selectedIndicatorId: filters.indicatorId ? parseInt(filters.indicatorId, 10) : null,
});

const resolveSelectedIndicator = async (filters) => {
  if (!filters.indicatorId) return null;

  const selected = {
    id: filters.indicatorId,
    full_name: filters.indicator,
  };

  try {
    const { data } = await axiosInstance.get(`indicators/${filters.indicatorId}/`);
    if (data && idsMatch(data.id || data.indicator_id, filters.indicatorId)) {
      return {
        id: data.id || data.indicator_id,
        full_name: data.full_name || data.name || data.indicator_name || filters.indicator,
      };
    }
  } catch (err) {
    // use control-panel fallback below
  }

  return selected;
};

const fetchIndicatorResults = async (ind, filters) => {
  const attempts = [
    { includeLocation: true, includeDatasource: true },
    { includeLocation: true, includeDatasource: false },
    { includeLocation: false, includeDatasource: true },
    { includeLocation: false, includeDatasource: false },
  ];

  const runAttempt = async (index, bestResults) => {
    if (index >= attempts.length) return bestResults;

    try {
      const params = buildDataQueryParams(ind.id, filters, attempts[index]);
      const { data } = await axiosInstance.get('data/', { params });
      const results = data.results || [];
      const nextBest = results.length > bestResults.length ? results : bestResults;
      const pair = pickPeriodPair(results);
      if (pair && pair.previous) {
        return results;
      }
      return runAttempt(index + 1, nextBest);
    } catch (err) {
      return runAttempt(index + 1, bestResults);
    }
  };

  return runAttempt(0, []);
};

const buildSelectionPlaceholder = (filters) => {
  if (!filters.indicatorId) return null;
  const displayName = filters.indicator && filters.indicator !== 'All Indicators'
    ? filters.indicator
    : 'Selected indicator';

  return {
    id: parseInt(filters.indicatorId, 10),
    title: `Focus: ${displayName}`,
    description: `Priority Signal Engine is focused only on ${displayName} for ${filters.location || 'the current location'}. Live comparison periods were not returned for this exact filter combination.`,
    recommendation: 'Confirm the indicator has published values for the chosen datasource and location, then refresh.',
    state: filters.location || 'National',
    indicator: displayName,
    priority_score: 60,
    type: 'warning',
    is_selected: true,
  };
};

const analyzeSelectedIndicator = async (ind, filters) => {
  const results = await fetchIndicatorResults(ind, filters);
  const pair = pickPeriodPair(results);
  const context = buildContext(filters);

  if (pair && pair.previous) {
    return buildInsightFromIndicator(ind, pair.current, pair.previous, context);
  }

  if (pair && pair.current) {
    return buildLimitedInsightFromIndicator(ind, pair.current, context);
  }

  return buildSelectionPlaceholder(filters);
};

/**
 * Priority Signals analyze exactly one indicator: the one currently selected
 * in the control panel. No peers, no history of prior selections.
 */
const generateAIInsights = async (filters) => {
  const safeFilters = filters || {};
  if (!safeFilters.indicatorId) return [];

  const selected = await resolveSelectedIndicator(safeFilters);
  if (!selected) {
    const placeholder = buildSelectionPlaceholder(safeFilters);
    return placeholder ? [placeholder] : [];
  }

  const insight = await analyzeSelectedIndicator(selected, safeFilters);
  if (!insight) return [];

  // Hard guarantee: never return another indicator's signal.
  if (!idsMatch(insight.id, safeFilters.indicatorId)) {
    const placeholder = buildSelectionPlaceholder(safeFilters);
    return placeholder ? [placeholder] : [];
  }

  return [{ ...insight, is_selected: true }];
};

export default generateAIInsights;
